import React from "react";
import styled from "styled-components";
import { SkeletonBox, SkeletonText, SkeletonCard } from "./SkeletonBox";

/**
 * GenreChildPageSkeleton - Non-animated skeleton for inner Genre/Category content pages
 * 
 * Matches layout of:
 * - genreChild/content.js (Back button, title, content grid)
 * - genreParent/content.js (Similar structure with children)
 */

var Container = styled.div`
  padding: 2rem 4rem;
  padding-bottom: 0;
  min-height: 100vh;
  background-color: #121212;
`;

var HeaderRow = styled.div`
  display: -webkit-box;
  display: -webkit-flex;
  display: -moz-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: horizontal;
  -webkit-box-direction: normal;
  -webkit-flex-direction: row;
  -moz-box-orient: horizontal;
  -moz-box-direction: normal;
  -ms-flex-direction: row;
  flex-direction: row;
  -webkit-box-align: center;
  -webkit-align-items: center;
  -moz-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  margin-bottom: 20px;
`;

var BackButtonSkeleton = styled.div`
  width: 48px;
  height: 48px;
  background-color: #262626;
  -webkit-border-radius: 12px;
  -moz-border-radius: 12px;
  border-radius: 12px;
  margin-left: 12px;
`;

var ContentGrid = styled.div`
  display: -webkit-box;
  display: -webkit-flex;
  display: -moz-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-flex-wrap: wrap;
  -ms-flex-wrap: wrap;
  flex-wrap: wrap;
  -webkit-box-pack: end;
  -webkit-justify-content: flex-end;
  -moz-box-pack: end;
  -ms-flex-pack: end;
  justify-content: flex-end;
  margin-top: 20px;
`;

var CardWrapper = styled.div`
  width: 20%;
  min-width: 140px;
  padding: 12px;
  padding-top: 24px;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  -webkit-flex-shrink: 0;
  -moz-flex-shrink: 0;
  -ms-flex-negative: 0;
  flex-shrink: 0;
`;

var SubGenreGrid = styled.div`
  display: -webkit-box;
  display: -webkit-flex;
  display: -moz-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-flex-wrap: wrap;
  -ms-flex-wrap: wrap;
  flex-wrap: wrap;
  margin-bottom: 30px;
`;

var SubGenreChip = styled.div`
  margin-left: 10px;
  margin-bottom: 10px;
`;

/**
 * Header with back button and title
 */
function HeaderSkeleton() {
  return (
    <HeaderRow>
      <BackButtonSkeleton />
      <SkeletonText width={200} size="xl" style={{ marginRight: 12 }} />
    </HeaderRow>
  );
}

/**
 * Sub-genre chips skeleton (for parent genres with children)
 */
function SubGenreChipsSkeleton({ count = 6 }) {
  var chips = [];
  var widths = [100, 80, 120, 90, 110, 85];

  for (var i = 0; i < count; i++) {
    chips.push(
      <SubGenreChip key={"subgenre_" + i}>
        <SkeletonBox
          width={widths[i % widths.length]}
          height={48}
          borderRadius={12}
        />
      </SubGenreChip>
    );
  }

  return (
    <SubGenreGrid>
      {chips}
    </SubGenreGrid>
  );
}

/**
 * Content cards grid skeleton
 */
function ContentGridSkeleton({ rows = 3, cols = 5 }) {
  var cards = [];
  var total = rows * cols;

  for (var i = 0; i < total; i++) {
    cards.push(
      <CardWrapper key={"card_" + i}>
        <div style={{
          width: "100%",
          height: 0,
          paddingTop: "140%",
          backgroundColor: "#262626",
          borderRadius: "16px",
        }} />
      </CardWrapper>
    );
  }

  return (
    <ContentGrid>
      {cards}
    </ContentGrid>
  );
}

/**
 * GenreChildPageSkeleton - Main export
 * @param {boolean} isParent - If true, shows sub-genre chips
 */
function GenreChildPageSkeleton({ isParent = false, rows = 3, cols = 5 }) {
  return (
    <Container>
      <HeaderSkeleton />
      {isParent && <SubGenreChipsSkeleton count={6} />}
      <ContentGridSkeleton rows={rows} cols={cols} />
    </Container>
  );
}

export { HeaderSkeleton, SubGenreChipsSkeleton, ContentGridSkeleton };
export default GenreChildPageSkeleton;
